import { APP_CFG } from '@/configs';
import { normalizeCoinType } from '@/utils';
import { parseF64Value } from '@/utils/f64';
import { Transaction } from '@mysten/sui/transactions';

// Base EXP unit as mentioned in the Sui Move code
const BASE_EXP_NORMALIZED_UNIT = 1_000_000;

export interface PlayerVipProfile {
	id: string;
	level: number;
	exp: number; // Raw exp value (in base units)
	expInUsd: number; // Normalized exp value (exp / BASE_EXP_NORMALIZED_UNIT)
	bonusMultiplier?: number;
	bonusMultiplierExpiryTimestamp?: number;
}

export interface VipLevelUpUsdReward {
	id: string;
	usdRewardAmount: number;
	usdCoinType: string;
	createdAtTimestamp: number;
	level: number;
}

export interface VipExpMultiplier {
	coinType: string;
	multiplier: number;
}

export interface VipLevelInfo {
	level: number;
	name: string;
	minExp: number;
	maxExp: number;
	cashbackRate: number;
	privileges: string[];
	color: string;
	bgColor: string;
	borderColor: string;
	levelUpBonus?: number; // USD bonus for reaching this level
}

// VIP Level configuration (you might want to fetch this from backend later)
export const VIP_LEVELS: VipLevelInfo[] = [
	{
		level: 1,
		name: 'Bronze',
		minExp: 100, // Start Bronze at $100 wagered
		maxExp: 1000,
		cashbackRate: 0.5,
		privileges: ['Basic Support', 'Standard Withdrawals'],
		color: 'from-warning to-warning/80',
		bgColor: 'bg-warning/10',
		borderColor: 'border-warning/30',
		levelUpBonus: 5, // $5 USD bonus
	},
	{
		level: 2,
		name: 'Silver',
		minExp: 1000,
		maxExp: 2500,
		cashbackRate: 1.0,
		privileges: ['Priority Support', 'Faster Withdrawals', 'Monthly Bonus'],
		color: 'from-gray-400 to-gray-600',
		bgColor: 'bg-muted/10',
		borderColor: 'border-muted/30',
		levelUpBonus: 15, // $15 USD bonus
	},
	{
		level: 3,
		name: 'Gold',
		minExp: 2500,
		maxExp: 5000,
		cashbackRate: 0.8,
		privileges: ['Priority Support', 'Faster Withdrawals', 'VIP Events'],
		color: 'from-warning to-warning/80',
		bgColor: 'bg-warning/10',
		borderColor: 'border-warning/30',
		levelUpBonus: 30, // $30 USD bonus
	},
	{
		level: 4,
		name: 'Platinum',
		minExp: 5000,
		maxExp: 10000,
		cashbackRate: 2.0,
		privileges: ['Dedicated Manager', 'Custom Limits', 'Daily Bonus', 'Beta Access'],
		color: 'from-purple-400 to-purple-600',
		bgColor: 'bg-secondary/10',
		borderColor: 'border-accent/30',
		levelUpBonus: 75, // $75 USD bonus
	},
	{
		level: 5,
		name: 'Diamond',
		minExp: 10000,
		maxExp: 25000,
		cashbackRate: 2.5,
		privileges: ['Personal Concierge', 'Unlimited Withdrawals', 'Hourly Bonuses', 'Exclusive Events'],
		color: 'from-blue-400 to-blue-600',
		bgColor: 'bg-primary/10',
		borderColor: 'border-secondary/30',
		levelUpBonus: 150, // $150 USD bonus
	},
	{
		level: 6,
		name: 'Elite',
		minExp: 25000,
		maxExp: Infinity,
		cashbackRate: 1.2,
		privileges: ['Personal Manager', 'Instant Withdrawals', 'Exclusive Tournaments', 'Custom Limits'],
		color: 'from-destructive to-destructive/80',
		bgColor: 'bg-destructive/10',
		borderColor: 'border-destructive/30',
		levelUpBonus: 300, // $300 USD bonus
	},
];

type SuiClientLike = {
	getObject: (...args: any[]) => Promise<any>;
	getDynamicFieldObject: (...args: any[]) => Promise<any>;
	getDynamicFields: (...args: any[]) => Promise<any>;
};

class VipRepository {
	// Make VIP_LEVELS accessible as a public property
	public readonly VIP_LEVELS = VIP_LEVELS;

	/**
	 * Get player's VIP profile from the blockchain
	 */
	async getPlayerVipProfile(client: SuiClientLike, playerAddress: string): Promise<PlayerVipProfile | null> {
		try {
			// Get the VipRegistry object
			const vipRegistry = await client.getObject({
				id: APP_CFG.VIP_REGISTRY_ID,
				options: {
					showContent: true,
				},
			});

			if (!vipRegistry.data?.content || vipRegistry.data.content.dataType !== 'moveObject') {
				throw new Error('VIP Registry not found or invalid');
			}

			// Get the PlayerVipProfile as a dynamic object field
			const playerProfileKey = {
				type: `${APP_CFG.SUIGAR_PACKAGE_ID}::vip::PlayerVipProfileKey`,
				value: playerAddress,
			};

			const playerProfile = await client.getDynamicFieldObject({
				parentId: APP_CFG.VIP_REGISTRY_ID,
				name: playerProfileKey,
			});

			if (!playerProfile.data?.content || playerProfile.data.content.dataType !== 'moveObject') {
				return {
					id: '',
					level: 0,
					exp: 0,
					expInUsd: 0,
				};
			}

			const fields = playerProfile.data.content.fields as any;
			const exp = parseInt(fields.exp);
			const expInUsd = exp / BASE_EXP_NORMALIZED_UNIT;

			return {
				id: fields.id.id,
				level: parseInt(fields.level),
				exp,
				expInUsd,
				bonusMultiplier: fields.bonus_multiplier ? parseFloat(fields.bonus_multiplier) : undefined,
				bonusMultiplierExpiryTimestamp: fields.bonus_multiplier_expiry_timestamp_ms
					? parseInt(fields.bonus_multiplier_expiry_timestamp_ms)
					: undefined,
			};
		} catch (error) {
			// 	console.error('Error fetching player VIP profile:', error);
			// 	console.error('Player address:', playerAddress);
			// 	console.error('VIP Registry ID:', APP_CFG.VIP_REGISTRY_ID);
			// 	console.error('Package ID:', APP_CFG.SUIGAR_PACKAGE_ID);
			return null;
		}
	}

	/**
	 * Get unclaimed VIP level-up rewards for a player
	 */
	async getUnclaimedVipRewards(client: SuiClientLike, playerAddress: string): Promise<VipLevelUpUsdReward[]> {
		try {
			const playerProfile = await this.getPlayerVipProfile(client, playerAddress);
			if (!playerProfile || !playerProfile.id) {
				return [];
			}

			const rewards: VipLevelUpUsdReward[] = [];

			// Check for rewards for each level up to current level
			for (let level = 1; level <= playerProfile.level; level++) {
				try {
					const rewardKey = {
						type: `${APP_CFG.SUIGAR_PACKAGE_ID}::vip::VipLevelUpUsdRewardKey`,
						value: level,
					};

					const reward = await client.getDynamicFieldObject({
						parentId: playerProfile.id,
						name: rewardKey,
					});

					if (reward.data?.content && reward.data.content.dataType === 'moveObject') {
						const fields = reward.data.content.fields as any;
						rewards.push({
							id: fields.id.id,
							level,
							usdRewardAmount: parseFloat(fields.usd_reward_amount),
							usdCoinType: fields.usd_coin_type.name,
							createdAtTimestamp: parseInt(fields.created_at_timestamp_ms),
						});
					}
				} catch (error) {
					// Reward doesn't exist for this level, continue
					continue;
				}
			}

			return rewards;
		} catch (error) {
			console.error('Error fetching unclaimed VIP rewards:', error);
			console.error('Player address:', playerAddress);
			return [];
		}
	}

	async getExpMultipliers(client: SuiClientLike): Promise<VipExpMultiplier[]> {
		if (!APP_CFG.VIP_REGISTRY_ID) {
			return [];
		}

		try {
			const dynamicFields = (await client.getDynamicFields({ parentId: APP_CFG.VIP_REGISTRY_ID })) as any;
			if (!dynamicFields?.data?.length) {
				return [];
			}

			const entries = (dynamicFields.data as any[]).filter((field) => field.name?.type?.endsWith('ExpMultiplierKey'));
			if (!entries.length) {
				return [];
			}

			const multipliers: VipExpMultiplier[] = [];
			for (const entry of entries as any[]) {
				try {
					const object = (await client.getObject({ id: entry.objectId, options: { showContent: true } })) as any;
					const content = object.data?.content;
					if (!content || content.dataType !== 'moveObject') {
						continue;
					}

					const fields = (content as any).fields ?? {};
					const rawCoinType =
						typeof entry.name?.value?.pos0?.name === 'string'
							? entry.name.value.pos0.name
							: typeof entry.name?.value?.pos0 === 'string'
								? entry.name.value.pos0
								: typeof entry.name?.value?.name === 'string'
									? entry.name.value.name
									: typeof entry.name?.value === 'string'
										? entry.name.value
										: '';

					const coinType = rawCoinType || (fields.coin_type?.fields?.name ?? fields.coin_type?.name ?? '');
					if (typeof coinType !== 'string' || !coinType.length) {
						continue;
					}

					const multiplierValue = parseF64Value(fields.multiplier ?? fields.value ?? null);
					if (!Number.isFinite(multiplierValue) || multiplierValue <= 0) {
						continue;
					}

					multipliers.push({ coinType: normalizeCoinType(coinType), multiplier: multiplierValue });
				} catch (error) {
					console.error('Error loading EXP multiplier', error, entry.objectId);
				}
			}

			return multipliers;
		} catch (error) {
			console.error('Error fetching EXP multipliers:', error);
			return [];
		}
	}

	/**
	 * Claim VIP level-up USD reward
	 */
	async claimVipLevelUpReward(level: number, playerAddress: string, signAndExecuteTransaction: (tx: Transaction) => Promise<any>): Promise<void> {
		const txb = new Transaction();

		txb.moveCall({
			arguments: [txb.object(APP_CFG.VIP_REGISTRY_ID), txb.pure.u64(level)],
			target: `${APP_CFG.SUIGAR_PACKAGE_ID}::vip::claim_level_up_usd_reward`,
		});

		const response = await signAndExecuteTransaction(txb);

		if (!response || (response as any).effects.status.status === 'failure') {
			const error = (response as any)?.effects?.status?.error || 'Transaction failed';
			throw new Error(`Failed to claim VIP reward: ${error}`);
		}
	}

	/**
	 * Get VIP level info by level number
	 */
	getVipLevelInfo(level: number): VipLevelInfo {
		if (level === 0) {
			return {
				level: 0,
				name: 'Newbie',
				minExp: 0,
				maxExp: 100, // Match the minExp of Bronze level
				cashbackRate: 0,
				privileges: [],
				color: 'from-gray-500 to-gray-700',
				bgColor: 'bg-muted/10',
				borderColor: 'border-muted/30',
			};
		}
		return VIP_LEVELS[level - 1] || VIP_LEVELS[VIP_LEVELS.length - 1];
	}

	/**
	 * Calculate progress to next level
	 */
	calculateProgressToNextLevel(
		currentExp: number,
		currentLevel: number,
	): {
		progressPercentage: number;
		expToNextLevel: number;
		nextLevelExp: number;
	} {
		const currentLevelInfo = this.getVipLevelInfo(currentLevel);
		const nextLevelInfo = this.getVipLevelInfo(currentLevel + 1);

		if (currentLevel >= VIP_LEVELS.length) {
			return {
				progressPercentage: 100,
				expToNextLevel: 0,
				nextLevelExp: currentExp,
			};
		}

		const expInCurrentLevel = currentExp - currentLevelInfo.minExp;
		const expNeededForNextLevel = nextLevelInfo.minExp - currentLevelInfo.minExp;
		const progressPercentage = Math.min((expInCurrentLevel / expNeededForNextLevel) * 100, 100);

		return {
			progressPercentage,
			expToNextLevel: Math.max(nextLevelInfo.minExp - currentExp, 0),
			nextLevelExp: nextLevelInfo.minExp,
		};
	}
}

export const vipRepository = new VipRepository();
